import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CreditCard, Loader2, CheckCircle, Sparkles, ShoppingBag } from 'lucide-react';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;
const MELANIN_ICON = "https://customer-assets.emergentagent.com/job_blackownedhub/artifacts/9kb68tzi_Melanin%20Icon%20-%20White%20with%20Black%20Background.png";

export default function MelaninPurchase({ onPurchaseComplete }) {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [pollingSessionId, setPollingSessionId] = useState(null);

  useEffect(() => {
    fetchPackages();
    
    // Check if returning from Stripe
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const purchaseStatus = urlParams.get('purchase');
    
    if (sessionId && purchaseStatus === 'success') {
      setPollingSessionId(sessionId);
      pollPaymentStatus(sessionId);
      
      // Clean up URL
      const newUrl = window.location.pathname + '?tab=wallet';
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await fetch(`${API}/melanin/packages`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPackages(data.packages);
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please check your wallet balance.');
      setPollingSessionId(null);
      return;
    }

    try {
      const response = await fetch(`${API}/melanin/checkout/status/${sessionId}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to check payment status');
      }

      const data = await response.json();

      if (data.payment_status === 'paid') {
        toast.success(`Payment successful! ${data.melanin_credited} Melanin added to your wallet.`);
        setPollingSessionId(null);
        onPurchaseComplete?.(data.melanin_credited);
        return;
      } else if (data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setPollingSessionId(null);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  const handlePurchase = async (packageId) => {
    setPurchasing(packageId);
    
    try {
      const response = await fetch(`${API}/melanin/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          package_id: packageId,
          origin_url: window.location.origin
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create checkout');
      }

      const data = await response.json();
      
      // Redirect to Stripe checkout
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (error) {
      toast.error(error.message || 'Failed to initiate purchase');
      setPurchasing(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-32 skeleton rounded-xl" />
        <div className="h-32 skeleton rounded-xl" />
      </div>
    );
  }

  // Show polling state
  if (pollingSessionId) {
    return (
      <div className="glass-card rounded-xl p-8 text-center">
        <Loader2 className="w-12 h-12 text-purple-400 animate-spin mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-white mb-2">Processing Payment</h3>
        <p className="text-zinc-400">
          Please wait while we verify your payment...
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 flex items-center justify-center">
          <ShoppingBag className="w-5 h-5 text-green-400" />
        </div>
        <div>
          <h3 className="font-semibold text-white">Buy Melanin</h3>
          <p className="text-zinc-500 text-sm">Purchase Melanin to boost your business or get badges</p>
        </div>
      </div>

      {/* Packages Grid */}
      <div className="grid grid-cols-2 gap-4">
        {packages.map((pkg, index) => (
          <motion.button
            key={pkg.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            onClick={() => handlePurchase(pkg.id)}
            disabled={purchasing}
            className={`p-5 rounded-xl border transition-all text-left group ${
              pkg.id === 'large' 
                ? 'bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/30 hover:border-purple-500/50'
                : 'bg-zinc-900/50 border-zinc-800 hover:border-zinc-700'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
            data-testid={`melanin-package-${pkg.id}`}
          >
            {pkg.id === 'large' && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400 text-xs font-medium mb-2">
                <Sparkles className="w-3 h-3" />
                Popular
              </span>
            )}
            
            <div className="flex items-center gap-2 mb-3">
              <img src={MELANIN_ICON} alt="" className="w-8 h-8 rounded-full" />
              <span className="text-2xl font-bold text-white">{pkg.melanin}</span>
              <span className="text-zinc-500">MEL</span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-lg font-semibold text-white">${(pkg.price || pkg.amount || 0).toFixed(2)}</span>
              {purchasing === pkg.id ? (
                <Loader2 className="w-5 h-5 text-purple-400 animate-spin" />
              ) : (
                <CreditCard className="w-5 h-5 text-zinc-500 group-hover:text-white transition-colors" />
              )}
            </div>
          </motion.button>
        ))}
      </div>

      {/* Info */}
      <div className="p-4 rounded-lg bg-zinc-900/30 border border-zinc-800">
        <div className="flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-400 shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="text-zinc-300 mb-1">Secure payments powered by Stripe</p>
            <p className="text-zinc-500">
              Melanin is credited instantly after payment. Use it for boosts, badges, or save for later.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
